import { newId, now } from '../../model/ids';
import { SampleBuffer } from '../../model/points';
import { CHANNELS, type CaptureView, type HoverSegment, type PointerKind, type Stroke, type Tool, type UUID } from '../../model/types';
import type { RawSample } from './samples';

export interface RunContext {
  pageId: UUID;
  notebookId: UUID;
  deviceId: UUID;
  pointerType: PointerKind;
  /** Page→client mapping; frozen at the start of the run. */
  view: CaptureView;
  /** performance.timeOrigin, injectable for tests/replay. */
  timeOrigin: number;
}

/**
 * Accumulates raw samples for one run (stroke or hover segment).
 * Each sample is converted to page units once and stored as float32;
 * nothing else is done to it.
 */
export class SampleRunBuilder {
  readonly buf = new SampleBuffer(512);
  readonly id: UUID = newId();
  readonly createdAt = now();
  private t0: number | null = null;

  constructor(readonly ctx: RunContext) {}

  add(s: RawSample): void {
    if (this.t0 === null) this.t0 = s.timeStamp;
    const { zoom, offsetX, offsetY } = this.ctx.view;
    this.buf.push(
      (s.clientX - offsetX) / zoom,
      (s.clientY - offsetY) / zoom,
      s.pressure,
      s.tiltX,
      s.tiltY,
      s.timeStamp - this.t0,
    );
  }

  /** Converts a sample to page units without storing it (predicted points). */
  project(s: RawSample, out: number[]): void {
    const { zoom, offsetX, offsetY } = this.ctx.view;
    out.push((s.clientX - offsetX) / zoom, (s.clientY - offsetY) / zoom, s.pressure, s.tiltX, s.tiltY, 0);
  }

  get count(): number {
    return this.buf.count;
  }

  get startTime(): number {
    return this.ctx.timeOrigin + (this.t0 ?? 0);
  }

  private run() {
    const c = this.ctx;
    const t = now();
    return {
      id: this.id,
      createdAt: this.createdAt,
      updatedAt: t,
      pageId: c.pageId,
      notebookId: c.notebookId,
      deviceId: c.deviceId,
      pointerType: c.pointerType,
      startTime: this.startTime,
      channels: CHANNELS,
      points: this.buf.snapshot(),
      capture: { ...c.view },
    };
  }

  toStroke(style: { tool: Tool; color: string; baseWidth: number }): Omit<Stroke, 'rev'> {
    return { ...this.run(), ...style };
  }

  toHover(prevStrokeId: UUID | null, nextStrokeId: UUID | null): Omit<HoverSegment, 'rev'> {
    return { ...this.run(), prevStrokeId, nextStrokeId };
  }
}
